import { useState, useLayoutEffect, ReactNode } from 'react';

interface FloatingPopupPosition {
  top: number;
  left: number;
}

export interface FloatingPopupAlign {
  minLeft?: number | React.RefObject<HTMLElement>;
  marginLeft?: number;
  marginWindow?: number;
}

interface FloatingPopupPositionProps {
  show: boolean;
  popupRef: React.RefObject<HTMLElement>;
  targetRef?: React.RefObject<HTMLElement>;
  align?: FloatingPopupAlign;
  children?: ReactNode;
}

const ZeroState: FloatingPopupPosition = { top: 0, left: 0 };

export const useFloatingPopupPosition = ({
  show,
  popupRef,
  targetRef,
  children,
  align,
}: FloatingPopupPositionProps): FloatingPopupPosition => {
  const [position, setPosition] = useState<FloatingPopupPosition>(ZeroState);

  useLayoutEffect(() => {
    const { marginLeft = 0, minLeft = 0, marginWindow = 10 } = align || {};
    const updatePosition = () => {
      if (targetRef?.current && popupRef.current) {
        const targetRect = targetRef.current.getBoundingClientRect();
        const popupRect = popupRef.current.getBoundingClientRect();
        const top = Math.max(
          0,
          Math.min(targetRect.top, window.innerHeight - popupRect.height) - marginWindow + window.scrollY
        );
        const minLeftPosition =
          typeof minLeft === 'number' ? minLeft : minLeft.current?.getBoundingClientRect().right ?? 0;
        const left = Math.max(minLeftPosition, targetRect.left + targetRect.width + marginLeft);
        setPosition({ top, left });
      }
    };
    if (show) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
    } else {
      setPosition(ZeroState);
    }
    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [show, children, popupRef, targetRef, align]);

  return position;
};
